/**
 * Lectura y limpieza del secreto (token de recuperación o código de activación) que llega en la URL de la página (LOG-5).
 *
 * Los enlaces nuevos de los correos lo traen en el fragmento (`#token=...`, `#codigo=...`), que el navegador no envía al
 * servidor. Los enlaces ya emitidos lo traen en la ruta (`/restablecer-contrasena/<token>`) o en la query
 * (`/activar-cuenta?codigo=...`) y siguen valiendo mientras el secreto esté vigente (la vigencia la fija el backend).
 *
 * El fragmento y la query se leen con `URLSearchParams`, que convierte `+` en espacio: los enlaces del backend escapan el
 * secreto con `Uri.EscapeDataString` (`+` llega como `%2B`); un enlace armado a mano con un `+` sin escapar lo corrompería.
 */

export type ClaveSecreto = 'token' | 'codigo';

export interface UbicacionUrl {
  pathname: string;
  search: string;
  hash: string;
}

export interface SecretoLeido {
  /** El secreto, o null si no vino o vino vacío. */
  secreto: string | null;
  /** Ruta sin el secreto (conserva el resto de la query y del fragmento). */
  rutaLimpia: string;
}

/**
 * Orden de prioridad: fragmento, parámetro de ruta, query. `paramRuta` es el valor que ya entregó React Router (`useParams`),
 * que viene decodificado: no se vuelve a decodificar. El fragmento y la query los decodifica `URLSearchParams` una sola vez.
 */
export function leerSecretoDeUrl(
  ubicacion: UbicacionUrl,
  clave: ClaveSecreto,
  paramRuta?: string
): SecretoLeido {
  const hash = new URLSearchParams(ubicacion.hash.replace(/^#/, ''));
  const query = new URLSearchParams(ubicacion.search.replace(/^\?/, ''));

  const delHash = hash.get(clave);
  const delaQuery = query.get(clave);
  const candidato = delHash || paramRuta || delaQuery || null;

  hash.delete(clave);
  query.delete(clave);

  // El parámetro de ruta es el último segmento (`/restablecer-contrasena/:token`); una barra final no lo cambia.
  const sinBarraFinal = ubicacion.pathname.replace(/\/+$/, '');
  const pathname = paramRuta ? sinBarraFinal.slice(0, sinBarraFinal.lastIndexOf('/')) || '/' : ubicacion.pathname;

  const search = query.toString();
  const fragmento = hash.toString();

  return {
    secreto: candidato,
    rutaLimpia: pathname + (search ? `?${search}` : '') + (fragmento ? `#${fragmento}` : ''),
  };
}

/**
 * Reemplaza la entrada actual del historial por la ruta limpia, conservando `history.state` (React Router guarda ahí
 * `{ usr, key, idx }`: un `replaceState` con estado vacío lo borraría). No usa `navigate`, que volvería a disparar los efectos.
 */
export function limpiarUrl(rutaLimpia: string, historial: History = window.history): void {
  historial.replaceState(historial.state, '', rutaLimpia);
}
